import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { VerticalStackedBarChart } from '@fluentui/react-charting';
import type { IVSChartDataPoint, IVerticalStackedChartProps } from '@fluentui/react-charting';
import { WidgetContainer } from '../WidgetContainer';
import { ChartThemeProvider } from '../ChartThemeProvider';
import { useGroupPurposeChartStyles } from './GroupPurposeChart.styles';
import type { BusinessGroupConsumption } from '@/types/reports';
import { BUSINESS_PURPOSES } from '@/mocks/reportsMockData';

export interface GroupPurposeChartProps {
  data: BusinessGroupConsumption[];
  title: string;
}

export const GroupPurposeChart = ({ data, title }: GroupPurposeChartProps) => {
  const styles = useGroupPurposeChartStyles();
  const { t } = useTranslation('reports');

  const sorted = useMemo(() => [...data].sort((a, b) => b.bookedHours - a.bookedHours), [data]);

  const chartData: IVerticalStackedChartProps[] = useMemo(() => {
    return sorted.map((group) => ({
      chartData: BUSINESS_PURPOSES.map(
        (purpose): IVSChartDataPoint => ({
          legend: purpose.name,
          data: group.purposeBreakdown[purpose.name] ?? 0,
          color: purpose.color,
        })
      ),
      xAxisPoint: group.name,
    }));
  }, [sorted]);

  const translatedTitle = t(title);

  return (
    <WidgetContainer title={translatedTitle}>
      <div className={styles.root} role="figure" aria-label={translatedTitle}>
        <ChartThemeProvider>
          <VerticalStackedBarChart
            data={chartData}
            chartTitle={translatedTitle}
            yAxisTitle="h"
            barWidth={24}
            enableReflow={true}
          />
        </ChartThemeProvider>
      </div>
    </WidgetContainer>
  );
};

GroupPurposeChart.displayName = 'GroupPurposeChart';
